import { LocationSearch } from '@griply/hooks';
import {
  CalendarEventDetails,
  CalendarEventFormFields,
  CalendarEventOccurrenceTarget,
  CalendarEventResponseStatus,
  CalendarEventWarning,
  ConnectionCalendar,
  ExternalConnection,
  ExternalConnectionContact,
} from '@griply/types';
import {
  getConnectionProvider,
  getExternalEventEditable,
  getProviderRecurrenceLimits,
  isCalendarEventFormValid,
  mapEventDetailsToFormState,
} from '@griply/utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Sizes } from 'shared/components/ui/button';
import { PopupMenu } from 'shared/components/ui/popup-menu';
import {
  DateFormatOptions,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { WeekDays } from 'shared/types/week-days';

import { EventApplyToOccurrencesDialog } from './event-apply-to-occurrences-dialog';
import { EventForm } from './event-form';
import * as Styled from './event-form.style';
import * as PopupStyled from './event-form-popup.style';
import { EventFormError, EventFormGone } from './event-form-status';
import { EventReadOnlyView } from './event-read-only-view';

const POPUP_WIDTH = 320;

export type EventFormPopupProps = {
  anchor: { top: number; left: number };
  // the instantly-available fields from the clicked grid event (or the create slot)
  seed: CalendarEventFormFields;
  // undefined while the detail fetch is in flight; null when the event no longer exists
  details?: CalendarEventDetails | null;
  isLoadingDetails?: boolean;
  isLoadingCalendars?: boolean;
  calendars: ConnectionCalendar[];
  selectableCalendars: ConnectionCalendar[];
  // only to resolve the target calendar's provider — the recurrence builder narrows its options by it
  connections: ExternalConnection[];
  contacts?: ExternalConnectionContact[];
  locationSearch: LocationSearch;
  // the zone the event's own calendar runs on; undefined until the calendars resolve
  calendarTimeZone?: string | null;
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  timeFormat: TimeFormatOptions;
  isEdit: boolean;
  isVideoCallLocked?: boolean;
  canReplaceVideoCall?: boolean;
  isSaving?: boolean;
  isError?: boolean;
  // the refusal the provider gave, already translated by the caller
  errorDescription?: string;
  warnings?: CalendarEventWarning[] | null;
  isResponding?: boolean;
  hasRespondError?: boolean;
  // where an invitee goes to counter-propose a time — the provider's own screen
  proposeTimeUrl?: string;
  // without premium every event opens read-only, whoever organises it
  canEditEvents?: boolean;
  // the event at the provider, offered next to the upgrade prompt
  eventUrl?: string;
  onSubmit: (
    fields: CalendarEventFormFields,
    occurrenceTarget: CalendarEventOccurrenceTarget,
  ) => void;
  onRespond: (status: CalendarEventResponseStatus) => void;
  onUnlockEdit: () => void;
  // header actions; the popup hands duplicate the fields as they stand on screen
  onDuplicate: (fields: CalendarEventFormFields) => void;
  onDelete: () => void;
  onRetry: () => void;
  onClose: () => void;
};

export const EventFormPopup: React.FC<EventFormPopupProps> = ({
  anchor,
  seed,
  details,
  isLoadingDetails,
  isLoadingCalendars,
  calendars,
  selectableCalendars,
  connections,
  contacts,
  locationSearch,
  calendarTimeZone,
  dateFormat,
  weekStartsOn,
  timeFormat,
  isEdit,
  isVideoCallLocked,
  canReplaceVideoCall,
  isSaving,
  isError,
  errorDescription,
  warnings,
  isResponding,
  hasRespondError,
  proposeTimeUrl,
  canEditEvents,
  eventUrl,
  onSubmit,
  onRespond,
  onUnlockEdit,
  onDuplicate,
  onDelete,
  onRetry,
  onClose,
}) => {
  const { t } = useTranslation();
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const [occurrencesPromptOpen, setOccurrencesPromptOpen] = useState(false);
  // set when the user leaves the popup before the detail fetch landed — see onOutside
  const [commitPending, setCommitPending] = useState(false);

  // Only what the user actually touched. The base underneath keeps re-deriving as the
  // detail fetch (and the calendars fetch, for create) resolves, so late-arriving fields
  // fill in without ever overwriting an edit in progress.
  const [edits, setEdits] = useState<Partial<CalendarEventFormFields>>({});

  const base = useMemo(
    () =>
      details ? mapEventDetailsToFormState(details, calendarTimeZone) : seed,
    [calendarTimeZone, details, seed],
  );
  const fields = useMemo(() => ({ ...base, ...edits }), [base, edits]);

  const onChangeFields = (partial: Partial<CalendarEventFormFields>) =>
    setEdits((previous) => ({ ...previous, ...partial }));

  const isDirty = !!Object.keys(edits).length;

  const isValid = useMemo(() => isCalendarEventFormValid(fields), [fields]);

  // Follows the picked calendar, not the event's original one: switching an event onto an Outlook
  // calendar has to narrow the builder before the user writes a rule that calendar cannot hold.
  const recurrenceLimits = useMemo(
    () =>
      getProviderRecurrenceLimits(
        fields.calendar
          ? getConnectionProvider(connections, fields.calendar.connectionId)
          : null,
      ),
    [connections, fields.calendar],
  );

  const hasWarnings = !!warnings?.length;
  const isGone = isEdit && details === null;
  const isEditLocked = isEdit && canEditEvents === false;
  // Same gate mobile's detail screen applies — an invitee OR a read-only connection — and
  // without premium nobody can edit; all three land read-only.
  const isReadOnly =
    isEditLocked ||
    (isEdit &&
      !!fields.calendar &&
      !getExternalEventEditable(fields.calendar, {
        connections,
        isOrganizer: details?.isOrganizer,
      }));

  const openOccurrencesPrompt = () => setOccurrencesPromptOpen(true);
  const closeOccurrencesPrompt = () => setOccurrencesPromptOpen(false);

  const submitForOccurrences = (
    occurrenceTarget: CalendarEventOccurrenceTarget,
  ) => {
    closeOccurrencesPrompt();
    onSubmit(fields, occurrenceTarget);
  };

  // Cancelling the recurrence question abandons the edit outright: the prompt is only
  // reached by leaving the popup, so returning to it with the edits still staged would
  // re-open this same dialog on the next outside click with no way out. Closing discards —
  // `edits` lives only here, and nothing was written.
  const cancelOccurrencesPrompt = () => {
    closeOccurrencesPrompt();
    onClose();
  };

  // both leave the popup, so neither can be caught by the leave-to-commit path below
  const onSelectDuplicate = () => onDuplicate(fields);
  const onSelectDelete = () => onDelete();

  // Dismissed before acting — a layered dialog would leave Escape closing the popup instead.
  const onSelectUnlockEdit = () => {
    onClose();
    onUnlockEdit();
  };

  const onSelectOpenInCalendar = () => {
    onClose();
    window.open(eventUrl, '_blank', 'noreferrer');
  };

  const commit = (loadedDetails: CalendarEventDetails | null | undefined) => {
    if (isEdit && loadedDetails?.isRecurring) {
      openOccurrencesPrompt();
      return;
    }
    onSubmit(fields, CalendarEventOccurrenceTarget.Single);
  };

  // There is no Save button: leaving the popup is the commit. An untouched, invalid or
  // status-screen popup just closes — only a valid edit writes, and a recurring one asks
  // which occurrences it applies to first.
  const onOutside = () => {
    if (occurrencesPromptOpen || isSaving || commitPending) {
      return;
    }
    if (
      isError ||
      hasWarnings ||
      isGone ||
      isReadOnly ||
      !isDirty ||
      !isValid
    ) {
      onClose();
      return;
    }
    // An edit patch is built from the full detail record. Committing on the seed alone
    // would send empty guests/description/reminders and wipe them at the provider — so
    // hold the popup open and write as soon as the fetch lands.
    if (isEdit && !details) {
      setCommitPending(true);
      return;
    }
    commit(details);
  };

  const content = isError ? (
    <EventFormError
      description={errorDescription}
      onRetry={onRetry}
      onCancel={onClose}
    />
  ) : isGone ? (
    <EventFormGone onClose={onClose} />
  ) : isReadOnly ? (
    <EventReadOnlyView
      fields={fields}
      dateFormat={dateFormat}
      timeFormat={timeFormat}
      weekStartsOn={weekStartsOn}
      isLoadingDetails={isLoadingDetails}
      isRecurring={details?.isRecurring}
      seriesRrule={details?.seriesRrule}
      videoCallUrl={details?.videoCallUrl}
      videoCallProviderName={details?.videoCallProviderName}
      descriptionHtml={details?.description}
      organizer={details?.organizer}
      isOrganizer={details?.isOrganizer}
      responseStatus={details?.selfResponseStatus}
      isResponding={isResponding}
      hasRespondError={hasRespondError}
      proposeTimeUrl={proposeTimeUrl}
      isEditLocked={isEditLocked}
      onRespond={onRespond}
      onOpenInCalendar={eventUrl ? onSelectOpenInCalendar : undefined}
      onUnlockEdit={onSelectUnlockEdit}
    />
  ) : (
    <EventForm
      fields={fields}
      onChangeFields={onChangeFields}
      calendars={calendars}
      selectableCalendars={selectableCalendars}
      contacts={contacts}
      locationSearch={locationSearch}
      recurrenceLimits={recurrenceLimits}
      dateFormat={dateFormat}
      weekStartsOn={weekStartsOn}
      timeFormat={timeFormat}
      isLoadingDetails={isLoadingDetails}
      isLoadingCalendars={isLoadingCalendars}
      isRecurring={details?.isRecurring}
      seriesRrule={details?.seriesRrule}
      isVideoCallLocked={isVideoCallLocked}
      videoCallUrl={details?.videoCallUrl}
      canReplaceVideoCall={canReplaceVideoCall}
      videoCallProviderName={details?.videoCallProviderName}
      descriptionHtml={details?.description}
      titleRef={titleRef}
      onDuplicate={isEdit ? onSelectDuplicate : undefined}
      onDelete={isEdit ? onSelectDelete : undefined}
    />
  );

  useEffect(() => {
    if (!commitPending || details === undefined) {
      return;
    }
    setCommitPending(false);
    details === null ? onClose() : commit(details);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once the awaited details resolve
  }, [commitPending, details]);

  return (
    <PopupMenu
      location={anchor}
      position="right-start"
      onOutside={onOutside}
      width={POPUP_WIDTH}
      // a new event opens ready to type its title; an existing one must not have its
      // title text put under a caret just for being opened
      initialFocus={isEdit ? undefined : titleRef}
      focusOnOpen
    >
      <PopupStyled.Container $saving={isSaving || commitPending}>
        {content}

        {hasWarnings && (
          <>
            <Styled.WarningBanner role="status">
              {warnings!.map((warning) => (
                <span key={warning.code}>
                  {t(`forms.event.warnings.${warning.code}`, {
                    defaultValue: warning.message,
                  })}
                </span>
              ))}
            </Styled.WarningBanner>
            <PopupStyled.Interactions>
              <Button size={Sizes.Medium} onClick={onClose} type="button">
                {t('forms.event.buttons.done.label')}
              </Button>
            </PopupStyled.Interactions>
          </>
        )}
      </PopupStyled.Container>

      {occurrencesPromptOpen && (
        <EventApplyToOccurrencesDialog
          onSelect={submitForOccurrences}
          onClose={cancelOccurrencesPrompt}
        />
      )}
    </PopupMenu>
  );
};
